import { Redirect, Route, Switch } from 'react-router-dom';
import routes from './routes';
import Root from './Root';
import PublicLayout from './layouts/PublicLayout';
import PrivateLayout from './layouts/PrivateLayout';
import HelpCenterLayout from './layouts/HelpCenterLayout';
import { getRoute, getRoutesByGroup } from './utils/route';
import InnerRoutes from './components/_common/InnerRoutes';
import SwitchInnerRoutes from './components/_common/SwitchInnerRoutes';
import './assets/styles/main.less';
import EmployeeFormProvider from './components/Employee/EmployeeFormProvider';
import WorkorderDetailProvider from './components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import SupplierFormProvider from './components/Suppliers/SupplierFormProvider';
import InsurerFormProvider from './components/Insurers/InsurerFormProvider';
import CustomerFormProvider from './components/Customers/CustomerFormProvider';
import TaskFormProvider from './pages/OperationalPages/TasksPage/TasksProvider';
import CompanyFormProvider from './components/Companies/CompaniesFormProvider';
import InventoryPartFormProvider from './pages/OperationalPages/InventoryPages/PartsPage/InventoryPartsProvider';
import ConsumableFormProvider from './pages/OperationalPages/InventoryPages/ConsumablesPage/ConsumableDetailsPage/ConsumableProvider';
import ReleasesPurchaseFormProvider from './pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import DirectSaleProvider from './pages/MovementsPage/DirectSalePage/DirectSaleProvider';
import InvoiceFormProvider from './components/Fiscal/Invoice/InvoiceFormProvider';
import PurchaseImportFileProvider from './pages/MovementsPage/PurchaseImportPage/PurchaseImportFileProvider';
import WorkorderChecklistProvider from './components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import BankAccountFormProvider from './pages/FinancialPages/AccountsPage/BankAccountsPage/BankAccountFormProvider';
import { InvoiceType } from './core/domain/invoice/invoices/invoices.enums';
import WorkOrderAdministrativeReport from './pages/Public/Reports/WorkOrderAdministrativeReport';
import InvoiceCFEFormProvider from './components/Fiscal/Invoice/InvoiceCFe/InvoiceCFEFormProvider';
import BillToPayReleaseDetailProvider from '@/components/Financial/BillToPay/ReleaseDetailProvider';
import BillToReceiveReleaseDetailProvider from './components/Financial/BillToReceive/ReleaseDetailProvider';
import CodeVerificationPage from './pages/CodeVerificationPage';

const App = () => {
  const app = getRoute(routes, 'app');

  const customers = getRoute(routes, 'customers');
  const customerDetails = getRoute(routes, ':customerId');

  const searchParts = getRoute(routes, 'searchParts');
  const searchPartDetail = getRoute(searchParts.routes, ':partId');

  const crm = getRoute(routes, 'crm');
  const crmShippingOptions = getRoute(crm.routes, 'shippingOptions');
  const crmSearchQuestionnaire = getRoute(crm.routes, 'searchQuestionnaire');

  const operational = getRoute(routes, 'operational');
  const inventory = getRoute(routes, 'inventory');
  const parts = getRoute(routes, 'parts');
  const partDetails = getRoute(parts.routes, ':partId');
  const consumables = getRoute(routes, 'consumables');
  const consumableDetails = getRoute(routes, ':consumableId');
  const groups = getRoute(routes, 'groups');
  const kits = getRoute(routes, 'kits');
  const tasks = getRoute(routes, 'tasks');
  const taskDetails = getRoute(routes, ':taskId');
  const areas = getRoute(routes, 'areas');

  const movements = getRoute(routes, 'movements');
  const workOrders = getRoute(movements.routes, 'workOrders');
  const workOrderDetails = getRoute(workOrders.routes, ':workOrderId');
  const workOrderChecklist = getRoute(movements.routes, 'workorderChecklist');
  const workOrderChecklistDetail = getRoute(
    workOrderChecklist.routes,
    ':checklistItemId',
  );
  const directSale = getRoute(movements.routes, 'directSale');
  const directSaleDetails = getRoute(movements.routes, ':directSaleId');
  const movementInventory = getRoute(movements.routes, 'inventory');
  const purchase = getRoute(routes, 'purchases');
  const purchasesRelease = getRoute(purchase.routes, 'purchasesReleases');
  const purchasesDetails = getRoute(purchase.routes, ':purchaseId');
  const itemsPurchase = getRoute(purchase.routes, 'itemsPurchase');
  const importPurchase = getRoute(routes, 'importPurchase');
  const partsLink24 = getRoute(routes, 'partsLink24');
  const stockCorrections = getRoute(routes, 'stockCorrections');
  const quotations = getRoute(routes, 'quotations');
  const diagnostics = getRoute(routes, 'diagnostics');
  const deliveryOfMaterials = getRoute(routes, 'deliveryOfMaterials');
  const stockTransfer = getRoute(movements.routes, 'stock-transfer');

  const registry = getRoute(routes, 'registry');
  const companies = getRoute(routes, 'companies');
  const companyDetails = getRoute(routes, ':companyId');
  const employees = getRoute(routes, 'employees');
  const employeeDetails = getRoute(routes, ':employeeId');
  const suppliers = getRoute(routes, 'suppliers');
  const supplierDetails = getRoute(routes, ':supplierId');
  const insurers = getRoute(routes, 'insurers');
  const insurerDetails = getRoute(routes, ':insurerId');

  const financial = getRoute(routes, 'financial');
  const accounts = getRoute(routes, 'accounts');
  const bankAccounts = getRoute(routes, 'bankAccounts');
  const bankAccountsDetail = getRoute(routes, ':bankAccountId');
  const documentTypes = getRoute(routes, 'documentTypes');
  const billToPay = getRoute(routes, 'billToPay');
  const releasesBillToPay = getRoute(routes, 'releaseBillToPay');
  const releasesBillToPayDetail = getRoute(
    routes,
    'detail/accountPayableId/:accountPayableId/installmentPayableId/:installmentPayableId',
  );
  const forecastPayable = getRoute(billToPay.routes, 'forecastPayable');
  const billToReceiver = getRoute(routes, 'billToReceiver');
  const releasesBillToReceiver = getRoute(routes, 'releaseBillToReceiver');
  const releasesBillToReceiverDetail = getRoute(
    routes,
    ':releaseId/installment/:installmentId',
  );
  const bankingEntry = getRoute(routes, 'bankingEntry');
  const bankSlip = getRoute(financial.routes, 'bankSlip');

  const fiscal = getRoute(routes, 'fiscal');
  const ncm = getRoute(routes, 'ncm');
  const sintegra = getRoute(routes, 'sintegra');
  const sped = getRoute(fiscal.routes, 'sped');
  const spedDocuments = getRoute(sped.routes, 'documents');
  const invoice = getRoute(fiscal.routes, 'invoice');
  const manifestationRecipient = getRoute(
    fiscal.routes,
    'manifestationRecipient',
  );

  const nfe = getRoute(invoice.routes, InvoiceType.NFE);
  const nfeDetails = getRoute(nfe.routes, ':invoiceId');
  const noteItens = getRoute(nfeDetails.routes, 'noteItens');

  const nfce = getRoute(invoice.routes, InvoiceType.NFC);
  const nfceDetails = getRoute(nfce.routes, ':invoiceId');
  const noteItensNfce = getRoute(nfceDetails.routes, 'noteItens');

  const nfse = getRoute(invoice.routes, InvoiceType.NFS);
  const nfseDetails = getRoute(nfse.routes, ':invoiceId');
  const noteItensNfse = getRoute(nfseDetails.routes, 'noteItens');

  const cfe = getRoute(fiscal.routes, 'cfe');
  const cfeDetail = getRoute(cfe.routes, ':invoiceId/:companyId');

  const history = getRoute(routes, 'history');
  const historyDetail = getRoute(history.routes, ':licensePlate');

  const managementCharts = getRoute(routes, 'managementCharts');

  const schedules = getRoute(routes, 'schedules');

  const informative = getRoute(routes, 'informative');

  const reports = getRoute(routes, 'reports');
  const reportInventory = getRoute(reports.routes, 'stock');
  const movementReports = getRoute(reports.routes, 'movements');
  const commissionReports = getRoute(reports.routes, 'commission');
  const fiscalReports = getRoute(reports.routes, 'fiscalReport');
  const productiveReports = getRoute(reports.routes, 'productive');
  const financialReports = getRoute(reports.routes, 'financial');
  const managerialReports = getRoute(reports.routes, 'managerial');
  const reportsPurchases = getRoute(reports.routes, 'reportsPurchases');

  const notifications = getRoute(routes, 'notifications');

  const helpCenter = getRoute(routes, 'helpCenter');
  const helpArea = getRoute(routes, ':helpAreaId');

  const publicRoutes = getRoutesByGroup(routes, 'public');
  const loginCallback = getRoute(routes, 'callback');
  const callbackAmazonLogin = getRoute(routes, 'callback/amazon');
  const forbidden = getRoute(routes, '403');
  const unauthorized = getRoute(routes, '401');
  const notFound = getRoute(routes, '404');
  const privacyPolicy = getRoute(routes, 'privacyPolicy');
  const expired = getRoute(routes, 'expired');
  const crmQuiz = getRoute(routes, 'crm-quiz/:requestCode');
  const validateAccount = getRoute(routes, 'account/validateEmail/:code');
  const publicReports = getRoute(routes, 'publicReports');
  const privateReports = getRoute(routes, 'privateReports');

  const friendlyReport = getRoute(routes, 'r');

  return (
    <Root>
      <Switch>
        <PrivateLayout path="/app" routes={app.routes}>
          <InnerRoutes route={app} />
          <SwitchInnerRoutes route={customers} />
          <Route
            path="/app/customers/:customerId"
            component={() => (
              <CustomerFormProvider>
                <SwitchInnerRoutes route={customerDetails} />
              </CustomerFormProvider>
            )}
          />
          <InnerRoutes route={searchParts} />
          <SwitchInnerRoutes route={searchPartDetail} />
          <SwitchInnerRoutes route={crm} />
          <SwitchInnerRoutes route={crmShippingOptions} />
          <SwitchInnerRoutes route={crmSearchQuestionnaire} />

          <InnerRoutes route={operational} />
          <SwitchInnerRoutes route={inventory} />
          <SwitchInnerRoutes route={parts} />
          <Route
            path="/app/operational/inventory/parts/:partId"
            component={() => (
              <InventoryPartFormProvider>
                <SwitchInnerRoutes route={partDetails} />
              </InventoryPartFormProvider>
            )}
          />
          <SwitchInnerRoutes route={consumables} />
          <Route
            path="/app/operational/inventory/consumables/:consumableId"
            component={() => (
              <ConsumableFormProvider>
                <SwitchInnerRoutes route={consumableDetails} />
              </ConsumableFormProvider>
            )}
          />
          <SwitchInnerRoutes route={groups} />
          <SwitchInnerRoutes route={kits} />
          <SwitchInnerRoutes route={tasks} />
          <Route
            path="/app/operational/tasks/:taskId"
            component={() => (
              <TaskFormProvider>
                <SwitchInnerRoutes route={taskDetails} />
              </TaskFormProvider>
            )}
          />
          <SwitchInnerRoutes route={areas} />
          <InnerRoutes route={movements} />
          <SwitchInnerRoutes route={workOrders} />
          <Route
            path="/app/movements/workOrders/:workOrderId/complements/:complementId"
            component={() => (
              <WorkorderDetailProvider>
                <SwitchInnerRoutes route={workOrderDetails} />
              </WorkorderDetailProvider>
            )}
          />
          <SwitchInnerRoutes route={workOrderChecklist} />
          <Route
            path="/app/movements/workorderChecklist/:checklistItemId"
            component={() => (
              <WorkorderChecklistProvider>
                <SwitchInnerRoutes route={workOrderChecklistDetail} />
              </WorkorderChecklistProvider>
            )}
          />
          <SwitchInnerRoutes route={directSale} />
          <Route
            path="/app/movements/directSale/:directSaleId"
            component={() => (
              <DirectSaleProvider>
                <InnerRoutes route={directSaleDetails} />
              </DirectSaleProvider>
            )}
          />
          <SwitchInnerRoutes route={movementInventory} />
          <SwitchInnerRoutes route={diagnostics} />
          <SwitchInnerRoutes route={stockCorrections} />
          <SwitchInnerRoutes route={quotations} />
          <Route
            path="/app/movements/purchases"
            component={() => (
              <PurchaseImportFileProvider>
                <SwitchInnerRoutes route={purchase} />
                <SwitchInnerRoutes route={importPurchase} />
                <SwitchInnerRoutes route={partsLink24} />
              </PurchaseImportFileProvider>
            )}
          />
          <SwitchInnerRoutes route={purchasesRelease} />
          <Route
            path="/app/movements/purchases/purchasesReleases/:purchaseId"
            component={() => (
              <ReleasesPurchaseFormProvider>
                <SwitchInnerRoutes route={purchasesDetails} />
                <SwitchInnerRoutes route={itemsPurchase} />
              </ReleasesPurchaseFormProvider>
            )}
          />

          <SwitchInnerRoutes route={deliveryOfMaterials} />
          <SwitchInnerRoutes route={stockTransfer} />

          <InnerRoutes route={registry} />
          <SwitchInnerRoutes route={companies} />
          <Route
            path="/app/registry/companies/:companyId"
            component={() => (
              <CompanyFormProvider>
                <SwitchInnerRoutes route={companyDetails} />
              </CompanyFormProvider>
            )}
          />
          <SwitchInnerRoutes route={employees} />
          <Route
            path="/app/registry/employees/:employeeId"
            component={() => (
              <EmployeeFormProvider>
                <SwitchInnerRoutes route={employeeDetails} />
              </EmployeeFormProvider>
            )}
          />
          <SwitchInnerRoutes route={insurers} />
          <Route
            path="/app/registry/insurers/:insurerId"
            component={() => (
              <InsurerFormProvider>
                <SwitchInnerRoutes route={insurerDetails} />
              </InsurerFormProvider>
            )}
          />
          <SwitchInnerRoutes route={suppliers} />
          <Route
            path="/app/registry/suppliers/:supplierId"
            component={() => (
              <SupplierFormProvider>
                <SwitchInnerRoutes route={supplierDetails} />
              </SupplierFormProvider>
            )}
          />

          <InnerRoutes route={financial} />
          <SwitchInnerRoutes route={accounts} />
          <SwitchInnerRoutes route={bankAccounts} />
          <Route
            path="/app/financial/accounts/bankAccounts/:bankAccountId"
            component={() => (
              <BankAccountFormProvider>
                <SwitchInnerRoutes route={bankAccountsDetail} />
              </BankAccountFormProvider>
            )}
          />

          <SwitchInnerRoutes route={documentTypes} />
          <SwitchInnerRoutes route={billToPay} />
          <SwitchInnerRoutes route={releasesBillToPay} />
          <Route
            path="/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/:accountPayableId/installmentPayableId/:installmentPayableId"
            component={() => (
              <BillToPayReleaseDetailProvider>
                <SwitchInnerRoutes route={releasesBillToPayDetail} />
              </BillToPayReleaseDetailProvider>
            )}
          />
          <SwitchInnerRoutes route={forecastPayable} />
          <SwitchInnerRoutes route={billToReceiver} />
          <SwitchInnerRoutes route={releasesBillToReceiver} />
          <Route
            path="/app/financial/billToReceiver/releaseBillToReceiver/:releaseId/installment/:installmentId"
            component={() => (
              <BillToReceiveReleaseDetailProvider>
                <SwitchInnerRoutes route={releasesBillToReceiverDetail} />
              </BillToReceiveReleaseDetailProvider>
            )}
          />
          <SwitchInnerRoutes route={bankingEntry} />
          <InnerRoutes route={bankSlip} />
          <InnerRoutes route={fiscal} />
          <SwitchInnerRoutes route={sped} />
          <InnerRoutes route={spedDocuments} />
          <Route
            path={`/app/fiscal/invoice/${InvoiceType.NFE}/:invoiceId`}
            component={() => (
              <InvoiceFormProvider>
                <SwitchInnerRoutes route={nfe} />
                <SwitchInnerRoutes route={nfeDetails} />
                <SwitchInnerRoutes route={noteItens} />
              </InvoiceFormProvider>
            )}
          />
          <Route
            path={`/app/fiscal/invoice/${InvoiceType.NFC}/:invoiceId`}
            component={() => (
              <InvoiceFormProvider>
                <SwitchInnerRoutes route={nfce} />
                <SwitchInnerRoutes route={nfceDetails} />
                <SwitchInnerRoutes route={noteItensNfce} />
              </InvoiceFormProvider>
            )}
          />
          <Route
            path={`/app/fiscal/invoice/${InvoiceType.NFS}/:invoiceId`}
            component={() => (
              <InvoiceFormProvider>
                <SwitchInnerRoutes route={nfse} />
                <SwitchInnerRoutes route={nfseDetails} />
                <SwitchInnerRoutes route={noteItensNfse} />
              </InvoiceFormProvider>
            )}
          />
          <Route
            path="/app/fiscal/cfe/:invoiceId/:companyId"
            component={() => (
              <InvoiceCFEFormProvider>
                <SwitchInnerRoutes route={cfeDetail} />
              </InvoiceCFEFormProvider>
            )}
          />
          <SwitchInnerRoutes route={ncm} />
          <SwitchInnerRoutes route={invoice} />
          <SwitchInnerRoutes route={sintegra} />
          <SwitchInnerRoutes route={manifestationRecipient} />

          <InnerRoutes route={history} />
          <SwitchInnerRoutes route={historyDetail} />
          <InnerRoutes route={managementCharts} />
          <InnerRoutes route={schedules} />
          <SwitchInnerRoutes route={informative} />
          <InnerRoutes route={reports} />
          <SwitchInnerRoutes route={reportInventory} />
          <SwitchInnerRoutes route={movementReports} />
          <SwitchInnerRoutes route={commissionReports} />
          <SwitchInnerRoutes route={fiscalReports} />
          <SwitchInnerRoutes route={productiveReports} />
          <SwitchInnerRoutes route={financialReports} />
          <SwitchInnerRoutes route={managerialReports} />
          <SwitchInnerRoutes route={reportsPurchases} />
          <SwitchInnerRoutes route={notifications} />
        </PrivateLayout>

        <Route {...loginCallback} />
        <Route {...callbackAmazonLogin} />
        <Route {...forbidden} />
        <Route {...unauthorized} />
        <Route {...notFound} />
        <Route {...privacyPolicy} />
        <Route {...expired} />
        <Route {...crmQuiz} />
        <Route {...validateAccount} />

        <Route
          path="/ar/:token"
          exact
          component={WorkOrderAdministrativeReport}
        />

        <HelpCenterLayout path="/helpCenter" routes={helpCenter.routes}>
          <SwitchInnerRoutes
            route={{
              ...helpCenter,
              routes: [helpCenter, ...helpCenter.routes],
            }}
          />
          <SwitchInnerRoutes route={helpArea} />
        </HelpCenterLayout>

        <PublicLayout path="/">
          {publicRoutes.map((route) => (
            <Route
              {...route}
              key={route.key}
              path={route.key === 'home' ? '/' : route.path}
            />
          ))}

          <SwitchInnerRoutes route={publicReports} />
          <SwitchInnerRoutes route={friendlyReport} />
          <SwitchInnerRoutes route={privateReports} />

          <Route
            path="/code-verification"
            exact
            component={CodeVerificationPage}
          />
        </PublicLayout>
      </Switch>
    </Root>
  );
};

export default App;
